// Build the query parameters for `GET /api/clients` used by both client
// fetch paths in the app (`refreshClients` and `refreshDynamicData`).
//
// Returns a `URLSearchParams` so callers can `.toString()` it onto their
// request URL.
//
// Feb 2026 — Rewritten to push include/exclude semantics down to the
// backend via `*_filter_mode` params instead of synthesizing the
// complement set on the client.  The old "send the complement"
// strategy combined badly with the backend's `$all` (labels) and
// `$in` + multi-provider (assigned_providers) semantics — see the
// regression test in `clientFilterParams.test.cjs`.  Now we always
// send the user's literal selection and let the backend negate via
// `$nin` / `$nor` when `mode === 'exclude'`.
//
// Sentinel `__none__` is no longer sent — the backend now interprets
// exclude mode directly so the sentinel is unnecessary.

export function buildClientQueryParams({
  refresh = false,
  clientStatusFilter,
  clientStatusFilterMode,
  clientProviderFilter,
  clientProviderFilterMode,
  selectedLabelFilter,
  selectedLabelFilterMode,
  selectedInsuranceFilter,
  selectedInsuranceFilterMode,
  clientSearch,
  clientsCurrentPage,
  clientsPerPage,
  clientsSortColumn,
  clientsSortDirection,
  userId,
  // `users` + `clientLabels` + `CLIENT_STATUSES` are no longer required
  // since the backend now handles mode-aware filtering.  Kept in the
  // signature so existing call sites don't need touching.
  users: _users,
  clientLabels: _clientLabels,
  insurances,
  CLIENT_STATUSES: _CLIENT_STATUSES,
}) {
  const params = new URLSearchParams();
  if (refresh) params.append('refresh', 'true');

  // ---- Status -----------------------------------------------------------
  if (clientStatusFilter && clientStatusFilter.length > 0) {
    params.append('status_filter', clientStatusFilter.join(','));
    if (clientStatusFilterMode === 'exclude') {
      params.append('status_filter_mode', 'exclude');
    }
  } else {
    // Feb 2026 — When the status filter is EMPTY, the UI shows
    // "All Statuses" but the backend defaults to hiding archived
    // clients (``include_archived=false``).  Result: a search by
    // name silently returned zero results when the only matches
    // were archived — the label promised "All", the behaviour was
    // "All except archived".  Send ``include_archived=true`` so
    // the empty/implicit "all" actually includes every status.
    params.append('include_archived', 'true');
  }

  // ---- Provider ---------------------------------------------------------
  // Resolve the 'mine' virtual bucket → current user id, peel
  // 'provider_<id>' wrappers down to bare ids, and split out the
  // 'unassigned' bucket which is conveyed via `include_unassigned`.
  if (clientProviderFilter && clientProviderFilter.length > 0) {
    const providerIds = [];
    let hasUnassigned = false;
    for (const p of clientProviderFilter) {
      if (p === 'mine') {
        if (userId) providerIds.push(userId);
      } else if (p === 'unassigned') {
        hasUnassigned = true;
      } else if (typeof p === 'string' && p.startsWith('provider_')) {
        providerIds.push(p.replace('provider_', ''));
      } else if (p) {
        providerIds.push(p);
      }
    }
    if (clientProviderFilterMode === 'exclude') {
      // Exclude: send the literal ids being excluded.  Backend uses
      // $nin.  `include_unassigned=true` means unassigned clients are
      // NOT in the excluded set, so we pass it through normally.
      if (providerIds.length > 0) {
        params.append('assigned_to', providerIds.join(','));
        params.append('assigned_to_mode', 'exclude');
      }
      // When the user explicitly excluded the "Unassigned" bucket we
      // must HIDE unassigned clients, i.e., do NOT
      // `include_unassigned`.  Otherwise (the unassigned bucket was
      // not picked) unassigned clients pass through naturally.
      if (!hasUnassigned) {
        params.append('include_unassigned', 'true');
      }
    } else {
      if (hasUnassigned) {
        params.append('include_unassigned', 'true');
      }
      if (providerIds.length > 0) {
        params.append('assigned_to', providerIds.join(','));
      }
    }
  }

  // ---- Labels -----------------------------------------------------------
  if (selectedLabelFilter && selectedLabelFilter.length > 0) {
    params.append('label_filter', selectedLabelFilter.join(','));
    if (selectedLabelFilterMode === 'exclude') {
      params.append('label_filter_mode', 'exclude');
    }
  }

  // ---- Insurance --------------------------------------------------------
  // In include mode we still expand selected parent carriers to also
  // match their sub-plans (Tufts → Tufts One Care, Tufts Medicare,
  // etc.) so the user's mental model ("everyone on Tufts") works.
  // In exclude mode we expand on the same rules — the backend will
  // hide clients matching any of the expanded names.
  if (selectedInsuranceFilter && selectedInsuranceFilter.length > 0) {
    const out = new Set();
    for (const sel of selectedInsuranceFilter) {
      out.add(sel);
      if (sel === '__self_pay__') continue;
      const parentDoc = (insurances || []).find(
        (i) => (i.name || '').toLowerCase() === (sel || '').toLowerCase()
      );
      if (parentDoc && Array.isArray(parentDoc.plans)) {
        for (const p of parentDoc.plans) {
          const pn = typeof p === 'string' ? p : (p && p.name) || '';
          if (pn) out.add(pn);
        }
      }
    }
    params.append('insurance_filter', [...out].join(','));
    if (selectedInsuranceFilterMode === 'exclude') {
      params.append('insurance_filter_mode', 'exclude');
    }
  }

  // ---- Search + pagination + sort --------------------------------------
  if (clientSearch && clientSearch.trim()) {
    params.append('search', clientSearch.trim());
  }
  if (clientsCurrentPage !== undefined && clientsCurrentPage !== null) {
    params.append('page', String(clientsCurrentPage));
  }
  if (clientsPerPage !== undefined && clientsPerPage !== null) {
    params.append(
      'page_size',
      String(clientsPerPage === 'all' ? 10000 : clientsPerPage)
    );
  }
  if (clientsSortColumn) params.append('sort_by', clientsSortColumn);
  if (clientsSortDirection) params.append('sort_order', clientsSortDirection);

  return params;
}
